import { useState } from 'react';

import { Box, IconButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';
import AppBar from '@/karehero-app/shell/AppBar';
import BackChevron from '@/karehero-app/shell/BackChevron';
import NotificationRow from '@/components/wrapped/NotificationRow';
import {
  NOTIFICATIONS,
  type NotificationGroup,
  type NotificationItem,
} from '@/karehero-app/notifications/Notifications.fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const SCREEN_PX = 2; // 16px gutter (matches the Figma 343px content width)

const SECTIONS: { id: NotificationGroup; label: string }[] = [
  { id: 'today', label: 'TODAY' },
  { id: 'week', label: 'THIS WEEK' },
  { id: 'earlier', label: 'EARLIER' },
];

// The Notification Centre: reverse-chronological notifications grouped by
// recency, each tappable (marks read + would deeplink). Reached from the bell
// in the app bar.
const NotificationsScreen = ({
  empty = false,
  onBack,
  onNavigate,
  bare = false,
}: {
  empty?: boolean;
  // Pops to wherever the user came from (the bell). Routing lives with the
  // caller; the copy is just Back. Omitted in Storybook.
  onBack?: () => void;
  // Follows a notification's deepLink when a row is tapped. Routing lives with
  // the caller (the shell); omitted in Storybook/handover, where rows just mark
  // read.
  onNavigate?: (path: string) => void;
  /** No status bar or app bar: the caller draws them. See PushScreen's `bar`. */
  bare?: boolean;
}) => {
  // A focused screen, not a tab: hide the bottom nav while it's open.
  useHideTabBar();

  const [items, setItems] = useState<NotificationItem[]>(
    empty ? [] : NOTIFICATIONS,
  );
  const visible = items;

  const markRead = (n: NotificationItem) =>
    setItems((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
    );
  const toggleRead = (n: NotificationItem) =>
    setItems((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, read: !x.read } : x)),
    );
  // Tapping a row marks it read, then follows its deepLink if one is set.
  const open = (n: NotificationItem) => {
    markRead(n);
    if (n.deepLink) onNavigate?.(n.deepLink);
  };

  return (
    <ScreenRoot>
      {!bare && (
        <>
          <StatusBar />
          {/* The shared back chevron and a centred title, as every pushed
              page. */}
          <AppBar
            title='Notifications'
            size='small'
            leading={onBack && <BackChevron label='Back' onClick={onBack} />}
          />
        </>
      )}

      {visible.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: SCREEN_PX,
          }}
        >
          <Box sx={{ mb: 2, color: 'text.disabled' }}>
            <Icon icon='bell' fontSize={32} />
          </Box>
          <PageHeader
            title='No notifications yet'
            description="We'll let you know when something needs your attention."
            isCentered
          />
        </Box>
      ) : (
        <Box
          sx={{ flex: 1, overflow: 'auto', px: SCREEN_PX, pt: 2, pb: '96px' }}
        >
          <Stack spacing={1}>
            {SECTIONS.map((section) => {
              const group = visible.filter((n) => n.group === section.id);
              if (group.length === 0) return null;
              return (
                <Box key={section.id}>
                  <Typography
                    variant='overline'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1, mb: 1 }}
                  >
                    {section.label}
                  </Typography>
                  <Stack spacing={1.5}>
                    {group.map((n) => {
                      return (
                        <NotificationRow
                          key={n.id}
                          title={n.title}
                          body={n.body}
                          when={n.when}
                          read={n.read}
                          onOpen={() => open(n)}
                          trailing={
                            <IconButton
                              aria-label={
                                n.read ? 'Mark as unread' : 'Mark as read'
                              }
                              size='small'
                              onClick={() => toggleRead(n)}
                            >
                              <Icon
                                icon={
                                  (n.read
                                    ? 'envelope-open'
                                    : 'envelope') as never
                                }
                                fontSize={18}
                              />
                            </IconButton>
                          }
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
    </ScreenRoot>
  );
};

export default NotificationsScreen;
